import { useEffect, useState } from 'react';
import { MeQuery } from '../../api';
import usersService from '../../services/users';
import tokenProvider from '../../utils/token';

type Me = NonNullable<MeQuery['me']>;

let cache: { token: string | null; request: Promise<Me> } | null = null;

const toMe = (raw: any): Me =>
  ({
    id: String(raw.id),
    email: raw.email,
    firstname: raw.firstName,
    lastname: raw.lastName,
    account: raw.account && { id: raw.account.id },
    accountEnabled: raw.accountEnabled,
    accountLocked: raw.accountLocked,
    emailVerified: raw.emailVerified,
    roles: raw.roles ?? [],
  } as Me);

const fetchMe = () => {
  const token = tokenProvider.getToken();

  if (!cache || cache.token !== token) {
    const request = usersService.getMe().then(toMe);
    cache = { token, request };
    request.catch(() => {
      cache = null;
    });
  }

  return cache.request;
};

export default function useMe(options?: { onCompleted?: (data: MeQuery) => void }) {
  const [data, setData] = useState<MeQuery | undefined>();
  const [error, setError] = useState<unknown>();
  const onCompleted = options?.onCompleted;

  useEffect(() => {
    let active = true;

    fetchMe()
      .then((me) => {
        if (!active) return;
        const result = { me };
        setData(result);
        onCompleted?.(result);
      })
      .catch((err) => active && setError(err));

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, error };
}
